import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  marketplaceDNAEngineDev105Api,
  type BackendAPI,
  type BackgroundJob,
  type DNADomainSpec,
  type FrontendFeature,
  type OutOfScopeItem,
  type ProviderInterface,
  type QualityGate,
  type SprintDashboard,
  type SprintValidation,
  type SuccessCriterion,
  type Dev105Status,
} from '../../api/marketplace-dna-engine-dev105';

type Tab = 'overview' | 'domains' | 'providers' | 'jobs' | 'frontend' | 'database' | 'apis' | 'gates' | 'status';
type Theme = 'light' | 'dark';

export function MarketplaceDNAEngineDev105Page() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SprintDashboard | null>(null);
  const [domains, setDomains] = useState<DNADomainSpec[]>([]);
  const [providers, setProviders] = useState<ProviderInterface[]>([]);
  const [jobs, setJobs] = useState<BackgroundJob[]>([]);
  const [features, setFeatures] = useState<FrontendFeature[]>([]);
  const [apis, setApis] = useState<BackendAPI[]>([]);
  const [gates, setGates] = useState<QualityGate[]>([]);
  const [outOfScope, setOutOfScope] = useState<OutOfScopeItem[]>([]);
  const [criteria, setCriteria] = useState<SuccessCriterion[]>([]);
  const [status, setStatus] = useState<Dev105Status | null>(null);
  const [validation, setValidation] = useState<SprintValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const card = dark ? 'border-teal-900/40 bg-zinc-900' : 'border-teal-200 bg-white';
  const muted = dark ? 'text-zinc-400' : 'text-slate-500';
  const shell = dark ? 'bg-zinc-950 text-zinc-100' : 'bg-teal-50/20 text-slate-900';
  const accent = dark ? 'text-teal-300' : 'text-teal-700';
  const tabActive = 'bg-teal-600 text-white';

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, dom, prov, jb, fe, api, gt, oos, crit, st] = await Promise.all([
        marketplaceDNAEngineDev105Api.dashboard(),
        marketplaceDNAEngineDev105Api.dnaDomains(),
        marketplaceDNAEngineDev105Api.serviceArchitecture(),
        marketplaceDNAEngineDev105Api.backgroundJobs(),
        marketplaceDNAEngineDev105Api.frontendFeatures(),
        marketplaceDNAEngineDev105Api.backendAPIs(),
        marketplaceDNAEngineDev105Api.qualityGates(),
        marketplaceDNAEngineDev105Api.outOfScope(),
        marketplaceDNAEngineDev105Api.successCriteria(),
        marketplaceDNAEngineDev105Api.status(),
      ]);
      setDashboard(dash); setDomains(dom); setProviders(prov); setJobs(jb);
      setFeatures(fe); setApis(api); setGates(gt); setOutOfScope(oos); setCriteria(crit); setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sprint center');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await marketplaceDNAEngineDev105Api.validate({
        every_asin_has_dna: true, dna_versions_immutable: true,
        snapshots_generated: true, scores_generated: true,
        benchmarks_operational: true, events_published: true,
        all_apis_documented: true, tests_pass: true, ci_passes: true,
        performance_budgets_achieved: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Marketplace DNA Engine passes all quality gates' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Validation failed'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'domains', label: 'DNA Domains' },
    { id: 'providers', label: 'Providers' }, { id: 'jobs', label: 'Background Jobs' },
    { id: 'frontend', label: 'Frontend' }, { id: 'database', label: 'Database' },
    { id: 'apis', label: 'APIs' }, { id: 'gates', label: 'Quality Gates' }, { id: 'status', label: 'Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>DEV-000105 · P0 · Digital Product Twin</p>
          <h1 className="text-2xl font-semibold">Marketplace DNA Engine</h1>
          <p className={`text-sm ${muted}`}>Single source of truth for every ASIN intelligence profile — continuously evolving digital twin</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/product-workspace-asin360-dev104" className={`${accent} hover:underline`}>DEV-000104</Link>
            <span className={muted}>→</span>
            <Link to="/products/B0CX123456/dna" className={`${accent} hover:underline`}>Live DNA Profile</Link>
            <span className={muted}>·</span>
            <Link to="/platform/marketplace-dna-bible" className={`${accent} hover:underline`}>MDB-000003</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>{dark ? 'Light' : 'Dark'}</button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-teal-300 bg-teal-50 p-3 text-sm text-teal-700 dark:border-teal-800 dark:bg-teal-950 dark:text-teal-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Digital Product Twin</h2>
            <p className={`mt-3 text-sm ${muted}`}>Every ASIN has DNA. DNA evolves, learns, records history, explains recommendations and predicts opportunities. DNA is never manually edited — it evolves through observation, measurement and marketplace synchronization.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'DNA Domains', value: dashboard.dna_domains },
              { label: 'Provider Interfaces', value: dashboard.provider_interfaces },
              { label: 'Background Jobs', value: dashboard.background_jobs },
              { label: 'Backend APIs', value: dashboard.backend_apis },
              { label: 'Objectives', value: dashboard.objectives },
              { label: 'Database Tables', value: dashboard.database_tables },
              { label: 'Quality Gates', value: dashboard.quality_gates },
              { label: 'Out of Scope', value: dashboard.out_of_scope },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
          <button type="button" disabled={busy} onClick={validate} className="rounded-lg bg-teal-600 px-4 py-2 text-sm text-white hover:bg-teal-500 disabled:opacity-50">Validate Quality Gates</button>
          {validation && (
            <div className={`rounded-xl border p-4 ${validation.valid ? 'border-emerald-300' : 'border-amber-300'} ${card}`}>
              <p className="font-semibold">{validation.valid ? 'DNA Engine Valid' : 'Gaps Detected'}</p>
              {validation.gaps && <ul className={`mt-2 list-disc pl-5 text-sm ${muted}`}>{validation.gaps.map((g) => <li key={g}>{g}</li>)}</ul>}
            </div>
          )}
        </div>
      )}

      {tab === 'domains' && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {domains.map((d) => (
            <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{d.name}</p>
              <p className={`text-xs ${muted}`}>{d.fields?.length ?? 0} tracked fields · independently versioned</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'providers' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {providers.map((p) => (
            <div key={p.key} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{p.name}</p>
              <p className={`font-mono text-xs ${accent}`}>{p.interface}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'jobs' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {jobs.map((j) => (
            <div key={j.key} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{j.name}</p>
              <p className={`font-mono text-xs ${muted}`}>{j.schedule}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'apis' && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className={muted}><th className="py-2 text-left">Method</th><th className="py-2 text-left">Path</th></tr></thead>
            <tbody>
              {apis.map((a) => (
                <tr key={a.key} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="py-2 font-mono text-xs">{a.method}</td>
                  <td className={`py-2 font-mono text-xs ${accent}`}>{a.path}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'gates' && (
        <div className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-2">
            {gates.map((g) => (
              <div key={g.key} className={`rounded-xl border p-4 ${card}`}>
                <p className="font-semibold">{g.name}</p>
                {g.blocking && <span className="mt-1 inline-block rounded bg-red-100 px-1.5 py-0.5 text-xs dark:bg-red-900">Blocking</span>}
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-semibold">Out of Scope</h3>
            <ul className={`mt-2 space-y-1 text-sm ${muted}`}>{outOfScope.map((o) => <li key={o.key}>{o.name}</li>)}</ul>
          </div>
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-5 ${card}`}>
          <dl className="grid gap-3 sm:grid-cols-2">
            <div><dt className={`text-xs uppercase ${muted}`}>Sprint</dt><dd className="font-semibold">{status.sprint_id}</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>DNA Domains</dt><dd>{status.dna_domains}</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>Digital Twin</dt><dd>{status.digital_product_twin ? 'Yes' : 'No'}</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>Production Ready</dt><dd>{status.production_ready ? 'Yes' : 'No'}</dd></div>
          </dl>
        </div>
      )}
    </div>
  );
}
